import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const lastUpdated = '12 June 2026';

  const sections = [
    {
      title: '1. Who we are',
      body: [
        'GovernWell ("GovernWell", "we", "us", or "our") provides a self-led governance review platform that helps organisations assess governance effectiveness, identify gaps, and build improvement plans.',
        'This Privacy Policy explains how we collect, use, store, and protect personal data when you use our website and platform. For the purposes of UK data protection law, including the UK GDPR and the Data Protection Act 2018, GovernWell is the data controller for the personal data described in this policy.',
      ],
    },
    {
      title: '2. Information we collect',
      list: [
        'Account information — name, email address, organisation name, and password when you create an account.',
        'Organisation & review data — the governance information, assessments, scores, notes, and documents you enter while conducting a review.',
        'Usage data — how you interact with the platform, including pages visited, features used, and actions taken.',
        'Technical data — IP address, browser type, device information, and similar diagnostic data.',
        'Communications — any messages you send us, for example via support enquiries.',
      ],
    },
    {
      title: '3. How we use your information',
      list: [
        'To provide, operate, and maintain the GovernWell platform.',
        'To create and manage your account and enable collaboration with your team.',
        'To generate governance reviews, reports, scores, and improvement roadmaps.',
        'To respond to your enquiries and provide customer support.',
        'To improve and develop our products, features, and services.',
        'To keep the platform secure and prevent fraud or misuse.',
        'To comply with our legal and regulatory obligations.',
      ],
    },
    {
      title: '4. Legal bases for processing',
      body: [
        'We process your personal data under the following legal bases: performance of a contract (to deliver the service you sign up for); our legitimate interests (to operate, secure, and improve the platform); your consent (where required, for example for certain communications); and compliance with legal obligations.',
      ],
    },
    {
      title: '5. How we share your information',
      body: [
        'We do not sell your personal data. We may share information with trusted service providers who help us run the platform (for example hosting and infrastructure providers), with members of your own organisation who you collaborate with, and with authorities where required by law.',
        'Any service providers acting on our behalf are required to process personal data only on our instructions and to keep it secure.',
      ],
    },
    {
      title: '6. Data retention',
      body: [
        'We retain your personal data for as long as your account is active or as needed to provide the service. We may retain certain information for longer where required to comply with legal obligations, resolve disputes, or enforce our agreements. When data is no longer required, we will delete or anonymise it.',
      ],
    },
    {
      title: '7. Data security',
      body: [
        'We use appropriate technical and organisational measures to protect personal data against unauthorised access, loss, or misuse. While we work hard to protect your information, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      title: '8. Your rights',
      body: [
        'Under UK data protection law you have rights over your personal data, including the right to access, correct, delete, or restrict its use, the right to object to processing, and the right to data portability. You may also withdraw consent where we rely on it.',
        'To exercise any of these rights, please contact us using the details below. You also have the right to lodge a complaint with the Information Commissioner\u2019s Office (ICO), the UK supervisory authority for data protection.',
      ],
    },
    {
      title: '9. Cookies',
      body: [
        'We use cookies and similar technologies to operate the website, remember your preferences, and understand how the platform is used. You can control cookies through your browser settings, though disabling them may affect how the platform works.',
      ],
    },
    {
      title: '10. International transfers',
      body: [
        'Where personal data is transferred outside the UK, we take steps to ensure it remains protected by appropriate safeguards in line with UK data protection law.',
      ],
    },
    {
      title: '11. Changes to this policy',
      body: [
        'We may update this Privacy Policy from time to time. When we do, we will revise the "last updated" date at the top of this page. We encourage you to review this policy periodically.',
      ],
    },
    {
      title: '12. Contact us',
      body: [
        'If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at hello@governwell.co.uk, GovernWell, London, United Kingdom.',
      ],
    },
  ];

  return (
    <div className="brand-theme min-h-screen overflow-x-hidden selection:bg-ink selection:text-lime">
      <Header />

      <section className="dot-pattern px-6 pt-20 pb-16 border-b border-ink/10">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex w-fit items-center gap-2 font-bold text-lg mb-8 hover:underline decoration-4 underline-offset-4 decoration-[#bcd445]"
          >
            <ArrowLeft className="w-5 h-5" /> Back to home
          </button>
          <div className="inline-block bg-white border border-ink/10 rounded-lg shadow-saas-sm px-4 py-2 mb-8 transform -rotate-2">
            <span className="font-bold text-lg uppercase tracking-wide">Legal</span>
          </div>
          <h1 className="font-bold tracking-tight text-5xl md:text-7xl mb-6 leading-[0.9]">
            PRIVACY<br />
            <span className="bg-lime px-4 ml-[-16px]">POLICY.</span>
          </h1>
          <p className="font-bold text-sm uppercase tracking-wide">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="px-6 py-20 max-w-4xl mx-auto">
        <div className="bg-cream border border-ink/10 rounded-lg shadow-saas p-6 md:p-10 mb-12">
          <p className="text-lg font-medium">
            This policy describes how GovernWell collects and uses personal data. By using our
            website and platform, you agree to the practices set out below.
          </p>
        </div>

        <div className="flex flex-col gap-12">
          {sections.map((section, i) => (
            <div key={i}>
              <h2 className="font-bold tracking-tight text-2xl md:text-3xl mb-4">{section.title}</h2>
              {section.body &&
                section.body.map((p, j) => (
                  <p key={j} className="text-lg font-medium mb-4 leading-relaxed">
                    {p}
                  </p>
                ))}
              {section.list && (
                <ul className="flex flex-col gap-3 mt-2">
                  {section.list.map((item, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 bg-white border border-ink/10 rounded-lg p-4 text-lg font-medium"
                    >
                      <span className="w-3 h-3 bg-lime border border-ink/10 rounded-lg shrink-0 mt-2" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
